// TODO: vitest component test (pages are covered by Playwright e2e)
import { useState } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Edit, Trash2, Archive } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, Badge, Skeleton, Modal, Input } from '@/components/ui'
import {
  useFixedAsset,
  useDeleteFixedAsset,
  useDisposeFixedAsset,
} from '@/hooks/useAccounting'
import { formatBaht } from '@/lib/money'
import { useDateFormatter, useLocalizationConfig } from '@/hooks/useDateFormatter'
import { localToday } from '@/lib/date'

export function FixedAssetDetailPage() {
  const { t } = useTranslation('accounting')
  const { t: tc } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const assetId = Number(id)
  const navigate = useNavigate()
  const formatDate = useDateFormatter()
  const { timezone } = useLocalizationConfig()

  const { data: asset, isLoading, isFetching } = useFixedAsset(assetId)
  const deleteAsset = useDeleteFixedAsset()
  const disposeAsset = useDisposeFixedAsset()

  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showDisposeModal, setShowDisposeModal] = useState(false)
  const [disposeDate, setDisposeDate] = useState(localToday(timezone))
  const [disposeAmount, setDisposeAmount] = useState('')

  const handleDelete = async () => {
    await deleteAsset.mutateAsync(assetId)
    navigate('/accounting/fixed-assets')
  }

  const handleDispose = async () => {
    await disposeAsset.mutateAsync({
      id: assetId,
      date: disposeDate,
      amount: Number(disposeAmount) || 0,
    })
    setShowDisposeModal(false)
  }

  const getStatusBadge = () => {
    if (!asset) return null
    switch (asset.status) {
      case 'active':
        return <Badge variant="success">{t('asset_statuses.active')}</Badge>
      case 'fully_depreciated':
        return <Badge variant="warning">{t('asset_statuses.fully_depreciated')}</Badge>
      case 'disposed':
        return <Badge variant="ghost">{t('asset_statuses.disposed')}</Badge>
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Breadcrumbs
          items={[
            { label: t('fixed_assets.title'), href: '/accounting/fixed-assets' },
            { label: '...' },
          ]}
        />
        <Card>
          <div className="space-y-4">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="h-10" />
            ))}
          </div>
        </Card>
      </div>
    )
  }

  if (!asset) return null

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[
          { label: t('fixed_assets.title'), href: '/accounting/fixed-assets' },
          { label: asset.name },
        ]}
      />

      <PageHeader
        title={asset.name}
        description={asset.description ?? undefined}
        isRefreshing={isFetching && !isLoading}
        // EP-106 Item 1 — Edit is the primary while the asset is live; once
        // disposed, Edit + Dispose both hide and Delete (always present)
        // promotes to solid, giving the intended 3→1 collapse.
        primaryAction={{
          key: 'edit',
          label: tc('actions.edit'),
          icon: Edit,
          to: `/accounting/fixed-assets/${assetId}/edit`,
          hidden: asset.status === 'disposed',
        }}
        overflowActions={[
          {
            key: 'dispose',
            label: t('fixed_assets.dispose_asset'),
            icon: Archive,
            onClick: () => setShowDisposeModal(true),
            hidden: asset.status === 'disposed',
          },
          {
            key: 'delete',
            label: tc('actions.delete'),
            icon: Trash2,
            onClick: () => setShowDeleteModal(true),
            destructive: true,
          },
        ]}
      />

      {/* Main Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <h3 className="font-semibold mb-4">{tc('labels.details')}</h3>
          <dl className="space-y-3">
            <div className="flex justify-between">
              <dt className="text-sm text-base-content/60">{t('labels.status')}</dt>
              <dd>{getStatusBadge()}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm text-base-content/60">{t('labels.acquisition_date')}</dt>
              <dd>{formatDate(asset.acquisition_date)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm text-base-content/60">{t('labels.acquisition_cost')}</dt>
              <dd className="font-medium tabular-nums">{formatBaht(asset.acquisition_cost, { locale: 'en', decimals: 2 })}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm text-base-content/60">{t('labels.salvage_value')}</dt>
              <dd className="tabular-nums">{formatBaht(asset.salvage_value, { locale: 'en', decimals: 2 })}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm text-base-content/60">{t('labels.useful_life')}</dt>
              <dd>{t('labels.useful_life_months_count', { count: asset.useful_life_months })}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm text-base-content/60">{t('labels.depreciation_method')}</dt>
              <dd>{t(`depreciation_methods.${asset.depreciation_method}`)}</dd>
            </div>
            {asset.property && (
              <div className="flex justify-between">
                <dt className="text-sm text-base-content/60">{t('labels.property')}</dt>
                <dd>{asset.property.name}</dd>
              </div>
            )}
            {asset.disposed_at && (
              <div className="flex justify-between">
                <dt className="text-sm text-base-content/60">{t('labels.disposed_at')}</dt>
                <dd>{formatDate(asset.disposed_at)}</dd>
              </div>
            )}
          </dl>
        </Card>

        <Card>
          <h3 className="font-semibold mb-4">{t('labels.net_book_value')}</h3>
          <dl className="space-y-3">
            <div className="flex justify-between">
              <dt className="text-sm text-base-content/60">{t('labels.acquisition_cost')}</dt>
              <dd className="tabular-nums">{formatBaht(asset.acquisition_cost, { locale: 'en', decimals: 2 })}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm text-base-content/60">{t('labels.accumulated_depreciation')}</dt>
              <dd className="tabular-nums text-error">
                ({formatBaht(asset.accumulated_depreciation, { locale: 'en', decimals: 2 })})
              </dd>
            </div>
            <div className="flex justify-between border-t pt-3">
              <dt className="font-semibold">{t('labels.net_book_value')}</dt>
              <dd className="font-bold text-lg tabular-nums">
                {formatBaht(asset.net_book_value, { locale: 'en', decimals: 2 })}
              </dd>
            </div>
          </dl>

          {asset.gl_account && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-sm text-base-content/60 mb-1">{t('labels.gl_account')}</p>
              <p className="font-mono text-sm">
                {asset.gl_account.code} — {asset.gl_account.name}
              </p>
            </div>
          )}
          {asset.depreciation_gl_account && (
            <div className="mt-3">
              <p className="text-sm text-base-content/60 mb-1">{t('labels.depreciation_account')}</p>
              <p className="font-mono text-sm">
                {asset.depreciation_gl_account.code} — {asset.depreciation_gl_account.name}
              </p>
            </div>
          )}
        </Card>
      </div>

      {/* Dispose Modal */}
      <Modal
        isOpen={showDisposeModal}
        onClose={() => setShowDisposeModal(false)}
        title={t('fixed_assets.dispose_asset')}
        footer={
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => setShowDisposeModal(false)}>
              {tc('actions.cancel')}
            </Button>
            <Button variant="error" onClick={handleDispose} loading={disposeAsset.isPending}>
              {t('fixed_assets.dispose_asset')}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <p>{t('fixed_assets.dispose_confirm')}</p>
          <Input
            label={t('fixed_assets.dispose_date')}
            type="date"
            value={disposeDate}
            onChange={(e) => setDisposeDate(e.target.value)}
            required
          />
          <Input
            label={t('fixed_assets.dispose_amount')}
            type="number"
            value={disposeAmount}
            onChange={(e) => setDisposeAmount(e.target.value)}
            placeholder="0.00"
            min="0"
            step="0.01"
            required
          />
        </div>
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title={tc('actions.delete')}
        footer={
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => setShowDeleteModal(false)}>
              {tc('actions.cancel')}
            </Button>
            <Button variant="error" onClick={handleDelete} loading={deleteAsset.isPending}>
              {tc('actions.delete')}
            </Button>
          </div>
        }
      >
        <p>{t('fixed_assets.delete_confirm')}</p>
      </Modal>
    </div>
  )
}
